import { useEffect, useMemo, useState } from 'react';

import {
  FARBEN,
  FARB_NAMEN,
  OHNE_GRUPPE,
  beispielGastro,
  eingabeAusKonfig,
  kachelFarbe,
  gleicheKonfig,
  istUngespeichert,
  kachelIcon,
  ladeSchnellwahl,
  markiereUngespeichert,
  speichereSchnellwahl,
  ungespeichertUnbekannt,
  vollabgleichSperre,
  type Farbe,
  type Schnellwahl,
  type SchnellwahlKonfig,
  type Warengruppe,
  MAX_KACHELN_ANZEIGE,
  sucheGesperrte,
  sucheKacheln,
} from '../schnellwahl';
import { STEUERSAETZE, formatiereEuro, parseEuro, type Steuersatz } from '../warenkorb';
import { ICON_NAMEN, ICON_TEXTE, Icon, type IconName } from './Icon';

function IconAuswahl(props: {
  wert: IconName | '';
  onChange: (icon: IconName | '') => void;
  label?: string;
}) {
  return (
    <label>
      {props.label ?? 'Icon'}
      <select
        value={props.wert}
        onChange={(e) => {
          props.onChange(e.target.value as IconName | '');
        }}
      >
        <option value="">– keins –</option>
        {ICON_NAMEN.map((n) => (
          <option key={n} value={n}>
            {ICON_TEXTE[n]}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Warengruppen als Chip-Leiste über den Kacheln; die Gruppenfarbe erscheint als Punkt. */
export function WarengruppenLeiste(props: {
  gruppen: readonly Warengruppe[];
  aktiv: string | null;
  onWahl: (name: string) => void;
}) {
  return (
    <div className="gruppen" role="tablist" aria-label="Warengruppen">
      {props.gruppen.map((g) => (
        <button
          key={g.name}
          type="button"
          role="tab"
          aria-selected={g.name === props.aktiv}
          className={`gruppe${g.name === props.aktiv ? ' aktiv' : ''}`}
          style={{ '--gruppe-farbe': FARBEN[g.farbe] } as React.CSSProperties}
          onClick={() => {
            props.onWahl(g.name);
          }}
        >
          {/* Gepflegtes Icon ersetzt den Farbpunkt (gleiche Farbe), sonst Punkt */}
          {g.icon === undefined ? (
            <span className="gruppe-punkt" aria-hidden="true" />
          ) : (
            <Icon name={g.icon} size={18} className="gruppe-icon" />
          )}
          <span>{g.name}</span>
        </button>
      ))}
    </div>
  );
}

/** Kachelraster der gewählten Gruppe; ein Tipp legt die Position in den Bon. */
export function SchnellwahlKacheln(props: {
  konfig: SchnellwahlKonfig;
  gruppe: string | null;
  /** Suchtext (Name oder EAN): ersetzt die Gruppenauswahl, solange er nicht leer ist. */
  suche?: string;
  /** Stückzahl der Kachel im aktuellen Bon (Anzeige „Im Bon“); ohne Angabe keine Anzeige. */
  imBon?: (k: Schnellwahl) => number;
  onWahl: (k: Schnellwahl) => void;
}) {
  const { konfig, gruppe } = props;
  const suche = props.suche?.trim() ?? '';
  if (konfig.kacheln.length === 0 && (konfig.gesperrt?.length ?? 0) === 0) {
    return (
      <p className="hinweis">
        Noch keine Schnellwahl – unter „Einrichtung“ Warengruppen und Artikel anlegen (oder das
        Gastro-Beispiel laden). Freie Beträge gehen über den Ziffernblock.
      </p>
    );
  }
  const alle =
    suche !== ''
      ? sucheKacheln(konfig.kacheln, suche)
      : // Im Profil ausgeblendete Artikel (P10) nur in der Gruppenansicht verbergen – Suche
        // und Scanner finden sie weiter.
        konfig.kacheln.filter(
          (k) => k.ausgeblendet !== true && (gruppe === null || k.gruppe === gruppe),
        );
  // Großer Stamm (z. B. 20.000 Artikel in einer Sammelgruppe): nur die ersten Kacheln
  // zeichnen, der Rest ist über Suche bzw. Scanner erreichbar.
  const kacheln = alle.slice(0, MAX_KACHELN_ANZEIGE);
  const gesperrt = sucheGesperrte(konfig, suche).slice(0, 20);
  return (
    <div className="kacheln">
      {kacheln.map((k, i) => {
        const imBon = props.imBon?.(k) ?? 0;
        const icon = kachelIcon(k, konfig.gruppen);
        return (
          <button
            key={`${String(i)}-${k.bezeichnung}`}
            type="button"
            className="kachel"
            style={{ '--gruppe-farbe': kachelFarbe(k, konfig.gruppen) } as React.CSSProperties}
            aria-label={`${k.bezeichnung}, ${formatiereEuro(k.bruttoCent)} €${imBon > 0 ? `, ${String(imBon)} im Bon` : ''}`}
            onClick={() => {
              props.onWahl(k);
            }}
          >
            <span className="name">
              {icon === null ? (
                <span className="kachel-punkt" aria-hidden="true" />
              ) : (
                <Icon name={icon} size={18} className="kachel-icon" />
              )}
              {k.bezeichnung}
            </span>
            <span className="kachel-fuss">
              <span className="preis">{formatiereEuro(k.bruttoCent)} €</span>
              {imBon > 0 && (
                <em className="im-bon" title="Im Bon">
                  {imBon}
                </em>
              )}
            </span>
          </button>
        );
      })}
      {gesperrt.map((k, i) => (
        <button
          key={`gesperrt-${String(i)}-${k.bezeichnung}`}
          type="button"
          className="kachel"
          disabled
          title={`Nicht verkaufbar: ${k.grund}`}
          aria-label={`${k.bezeichnung}, gesperrt: ${k.grund}`}
        >
          <span className="name">{k.bezeichnung}</span>
          <span className="kachel-fuss">
            <span className="preis">Gesperrt – {k.grund}</span>
          </span>
        </button>
      ))}
      {alle.length > kacheln.length && (
        <p className="hinweis">
          {`${String(alle.length - kacheln.length)} weitere Artikel – bitte suchen oder scannen.`}
        </p>
      )}
      {kacheln.length === 0 && gesperrt.length === 0 && (
        <p className="hinweis">
          {suche === '' ? 'Keine Artikel in dieser Gruppe.' : `Nichts gefunden für „${suche}“.`}
        </p>
      )}
    </div>
  );
}

function farbfeld(f: Farbe): React.CSSProperties {
  return {
    display: 'inline-block',
    width: '1rem',
    height: '1rem',
    borderRadius: '4px',
    background: FARBEN[f],
    verticalAlign: 'middle',
    marginRight: '0.3rem',
  };
}

/** Warengruppen und Kacheln je Kasse auf diesem Gerät pflegen (Einrichtung). */
export function SchnellwahlEditor(props: {
  kassenId: string;
  /** Konfiguration an den Server übertragen (Betreiber-Token); liefert eine Meldung. */
  onUebertragen?: (konfig: SchnellwahlKonfig) => Promise<string>;
  /** Serverstamm holen (Kassenschlüssel); null, wenn keiner hinterlegt ist. */
  onVomServer?: () => Promise<SchnellwahlKonfig | null>;
}) {
  const { kassenId } = props;
  const [serverMeldung, setServerMeldung] = useState<{ text: string; fehler: boolean } | null>(
    null,
  );
  const [konfig, setKonfig] = useState<SchnellwahlKonfig>(() =>
    ladeSchnellwahl(localStorage, kassenId),
  );
  const [gruppeForm, setGruppeForm] = useState<{
    name: string;
    farbe: Farbe;
    icon: IconName | '';
  }>({ name: '', farbe: 'blau', icon: '' });
  const [form, setForm] = useState<{
    bezeichnung: string;
    preis: string;
    steuersatz: Steuersatz;
    steuersatzAusserHaus: Steuersatz | '';
    gruppe: string;
    icon: IconName | '';
  }>({
    bezeichnung: '',
    preis: '',
    steuersatz: '20',
    steuersatzAusserHaus: '',
    gruppe: '',
    icon: '',
  });
  const [fehler, setFehler] = useState<string | null>(null);
  /** true, wenn am Server ein Artikelstamm liegt – dann zeigt der Verkauf diesen. */
  const [amServer, setAmServer] = useState(false);
  /** Lokale Änderungen, die noch nicht übertragen sind. */
  const [ungespeichert, setUngespeichert] = useState(() =>
    istUngespeichert(localStorage, kassenId),
  );

  /** PB-6: Vollabgleich bei großem Stamm gesperrt (Hinweistext) bzw. `null`. */
  const uebertragenGesperrt = useMemo(
    () => (props.onUebertragen === undefined ? null : vollabgleichSperre(eingabeAusKonfig(konfig))),
    [konfig, props.onUebertragen],
  );

  /** Änderung im Editor: lokal speichern und als „noch nicht am Server“ markieren. */
  function speichern(neu: SchnellwahlKonfig): void {
    setKonfig(neu);
    speichereSchnellwahl(localStorage, kassenId, neu);
    markiereUngespeichert(localStorage, kassenId, true);
    setUngespeichert(true);
  }

  /** Serverstand übernehmen (Laden oder nach dem Übertragen): Editor = Verkauf. */
  function uebernehmeServerstand(neu: SchnellwahlKonfig): void {
    setKonfig(neu);
    speichereSchnellwahl(localStorage, kassenId, neu);
    markiereUngespeichert(localStorage, kassenId, false);
    setUngespeichert(false);
    setAmServer(true);
  }

  // Beim Öffnen den Serverstand holen: ohne offene Änderungen übernehmen, damit
  // der Editor zeigt, was der Verkauf zeigt; mit offenen Änderungen nur hinweisen.
  const vomServer = props.onVomServer;
  useEffect(() => {
    if (vomServer === undefined) {
      return;
    }
    let aktuell = true;
    vomServer()
      .then((k) => {
        if (!aktuell || k === null) {
          return;
        }
        setAmServer(true);
        const lokal = ladeSchnellwahl(localStorage, kassenId);
        // Geräte aus der Zeit vor der Markierung: abweichender lokaler Stand gilt als offen,
        // damit er nicht stillschweigend überschrieben wird.
        if (ungespeichertUnbekannt(localStorage, kassenId)) {
          const offen = lokal.kacheln.length > 0 && !gleicheKonfig(lokal, k);
          markiereUngespeichert(localStorage, kassenId, offen);
          setUngespeichert(offen);
        }
        if (!istUngespeichert(localStorage, kassenId)) {
          setKonfig(k);
          speichereSchnellwahl(localStorage, kassenId, k);
        }
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [vomServer, kassenId]);

  function gruppeHinzufuegen(): void {
    const name = gruppeForm.name.trim();
    if (name === '' || konfig.gruppen.some((g) => g.name === name)) {
      setFehler('Gruppenname fehlt oder existiert schon');
      return;
    }
    setFehler(null);
    speichern({
      ...konfig,
      gruppen: [
        ...konfig.gruppen,
        {
          name,
          farbe: gruppeForm.farbe,
          ...(gruppeForm.icon === '' ? {} : { icon: gruppeForm.icon }),
        },
      ],
    });
    setGruppeForm({ ...gruppeForm, name: '' });
  }

  function gruppeFarbe(name: string, farbe: Farbe): void {
    speichern({
      ...konfig,
      gruppen: konfig.gruppen.map((g) => (g.name === name ? { ...g, farbe } : g)),
    });
  }

  function gruppeIcon(name: string, icon: IconName | ''): void {
    speichern({
      ...konfig,
      gruppen: konfig.gruppen.map((g) => {
        if (g.name !== name) {
          return g;
        }
        // Übrige Angaben (z. B. Steuersatz-Vorschlag der Gruppe) bleiben erhalten.
        const rest: Warengruppe = { ...g };
        delete rest.icon;
        return icon === '' ? rest : { ...rest, icon };
      }),
    });
  }

  function gruppeEntfernen(name: string): void {
    if (
      !window.confirm(
        `Gruppe „${name}“ samt ${String(konfig.kacheln.filter((k) => k.gruppe === name).length)} Kachel(n) entfernen?`,
      )
    ) {
      return;
    }
    speichern({
      ...konfig,
      gruppen: konfig.gruppen.filter((g) => g.name !== name),
      kacheln: konfig.kacheln.filter((k) => k.gruppe !== name),
    });
  }

  function kachelHinzufuegen(): void {
    const preis = parseEuro(form.preis);
    const gruppe = form.gruppe !== '' ? form.gruppe : (konfig.gruppen[0]?.name ?? OHNE_GRUPPE);
    if (form.bezeichnung.trim() === '' || preis === null || preis <= 0) {
      setFehler('Bezeichnung und Preis (z. B. 2,50) angeben');
      return;
    }
    setFehler(null);
    const gruppen = konfig.gruppen.some((g) => g.name === gruppe)
      ? konfig.gruppen
      : [...konfig.gruppen, { name: gruppe, farbe: 'blau' as Farbe }];
    speichern({
      ...konfig,
      gruppen,
      kacheln: [
        ...konfig.kacheln,
        {
          bezeichnung: form.bezeichnung.trim(),
          bruttoCent: preis,
          steuersatz: form.steuersatz,
          gruppe,
          ...(form.icon === '' ? {} : { icon: form.icon }),
          ...(form.steuersatzAusserHaus === '' || form.steuersatzAusserHaus === form.steuersatz
            ? {}
            : { steuersatzAusserHaus: form.steuersatzAusserHaus }),
        },
      ],
    });
    setForm({ ...form, bezeichnung: '', preis: '', gruppe });
  }

  function verschiebe(liste: Schnellwahl[], i: number, richtung: -1 | 1): void {
    const j = i + richtung;
    const a = liste[i];
    const b = liste[j];
    if (a === undefined || b === undefined) {
      return;
    }
    const neu = [...liste];
    neu[i] = b;
    neu[j] = a;
    speichern({ ...konfig, kacheln: neu });
  }

  return (
    <div className="karte">
      <h3>Schnellwahl (Kasse {kassenId})</h3>
      <div className="zeile">
        {props.onVomServer !== undefined && (
          <button
            className="taste zweit"
            onClick={() => {
              props
                .onVomServer?.()
                .then((k) => {
                  if (k === null) {
                    setServerMeldung({
                      text: 'Am Server ist noch kein Artikelstamm hinterlegt.',
                      fehler: false,
                    });
                  } else {
                    uebernehmeServerstand(k);
                    setServerMeldung({
                      text: 'Artikelstamm vom Server übernommen.',
                      fehler: false,
                    });
                  }
                })
                .catch((e: unknown) => {
                  setServerMeldung({
                    text: e instanceof Error ? e.message : String(e),
                    fehler: true,
                  });
                });
            }}
          >
            Vom Server laden
          </button>
        )}
        {props.onUebertragen !== undefined && (
          <button
            className="taste"
            disabled={konfig.kacheln.length === 0 || uebertragenGesperrt !== null}
            onClick={() => {
              if (uebertragenGesperrt !== null) {
                return;
              }
              props
                .onUebertragen?.(konfig)
                .then(async (text) => {
                  setServerMeldung({ text, fehler: false });
                  // Neu laden: die IDs und Reihenfolge kommen vom Server.
                  const k = await props.onVomServer?.();
                  if (k !== undefined && k !== null) {
                    uebernehmeServerstand(k);
                  } else {
                    markiereUngespeichert(localStorage, kassenId, false);
                    setUngespeichert(false);
                    setAmServer(true);
                  }
                })
                .catch((e: unknown) => {
                  setServerMeldung({
                    text: e instanceof Error ? e.message : String(e),
                    fehler: true,
                  });
                });
            }}
          >
            Auf den Server übertragen (alle Kassen des Unternehmers)
          </button>
        )}
      </div>
      {uebertragenGesperrt !== null && <p className="warnung">{uebertragenGesperrt}</p>}
      {serverMeldung !== null && (
        <p className={serverMeldung.fehler ? 'fehler' : 'ok'}>{serverMeldung.text}</p>
      )}
      {amServer && ungespeichert && (
        <p className="warnung">
          Änderungen noch nicht am Server – der Verkauf zeigt weiter den Artikelstamm vom Server.
          „Auf den Server übertragen“ übernimmt sie für alle Kassen des Unternehmers, „Vom Server
          laden“ verwirft sie.
        </p>
      )}
      <p className="hinweis">
        {amServer
          ? 'Der Artikelstamm liegt am Server (auch im Backoffice pflegbar); der Verkauf zeigt immer den Serverstand. '
          : 'Noch kein Artikelstamm am Server – der Verkauf zeigt die Kacheln dieses Geräts. '}
        Warengruppen erscheinen als Spalte im Verkauf, die Artikel einer Gruppe als farbige Kacheln.
        {konfig.kacheln.length === 0 && (
          <>
            {' '}
            <button
              className="taste zweit"
              onClick={() => {
                // Datenstand behalten: ersetzt nur die Kacheln dieses Geräts.
                speichern({
                  ...beispielGastro(),
                  ...(konfig.basisVersion === undefined
                    ? {}
                    : { basisVersion: konfig.basisVersion }),
                });
              }}
            >
              Gastro-Beispiel laden
            </button>
          </>
        )}
      </p>

      <h3>Warengruppen</h3>
      <div className="zeile">
        <label>
          Name
          <input
            value={gruppeForm.name}
            onChange={(e) => {
              setGruppeForm({ ...gruppeForm, name: e.target.value });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                gruppeHinzufuegen();
              }
            }}
          />
        </label>
        <label>
          Farbe
          <select
            value={gruppeForm.farbe}
            onChange={(e) => {
              setGruppeForm({ ...gruppeForm, farbe: e.target.value as Farbe });
            }}
          >
            {FARB_NAMEN.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <IconAuswahl
          wert={gruppeForm.icon}
          onChange={(icon) => {
            setGruppeForm({ ...gruppeForm, icon });
          }}
        />
        <button className="taste" onClick={gruppeHinzufuegen}>
          Gruppe hinzufügen
        </button>
      </div>
      {konfig.gruppen.length > 0 && (
        <table className="liste">
          <thead>
            <tr>
              <th>Gruppe</th>
              <th>Farbe</th>
              <th>Icon</th>
              <th className="betrag">Artikel</th>
              <th className="aktionen" />
            </tr>
          </thead>
          <tbody>
            {konfig.gruppen.map((g) => (
              <tr key={g.name}>
                <td>
                  <span style={farbfeld(g.farbe)} />
                  {g.name}
                </td>
                <td>
                  <select
                    value={g.farbe}
                    onChange={(e) => {
                      gruppeFarbe(g.name, e.target.value as Farbe);
                    }}
                  >
                    {FARB_NAMEN.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={g.icon ?? ''}
                    aria-label={`Icon der Gruppe ${g.name}`}
                    onChange={(e) => {
                      gruppeIcon(g.name, e.target.value as IconName | '');
                    }}
                  >
                    <option value="">– kein Icon –</option>
                    {ICON_NAMEN.map((n) => (
                      <option key={n} value={n}>
                        {ICON_TEXTE[n]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="betrag">
                  {konfig.kacheln.filter((k) => k.gruppe === g.name).length}
                </td>
                <td className="aktionen">
                  <button
                    className="taste zweit"
                    title="Gruppe entfernen"
                    aria-label={`Gruppe ${g.name} entfernen`}
                    onClick={() => {
                      gruppeEntfernen(g.name);
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Artikel-Kacheln</h3>
      <div className="zeile">
        <label>
          Gruppe
          <select
            value={form.gruppe}
            onChange={(e) => {
              // USt-Vorschlag der Gruppe übernehmen (Lebensmittel 10 %, Nonfood 20 % …)
              const ust = konfig.gruppen.find((g) => g.name === e.target.value)?.steuersatz;
              setForm({
                ...form,
                gruppe: e.target.value,
                ...(ust === undefined ? {} : { steuersatz: ust }),
              });
            }}
          >
            {konfig.gruppen.length === 0 && <option value="">{OHNE_GRUPPE}</option>}
            {konfig.gruppen.map((g) => (
              <option key={g.name} value={g.name}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bezeichnung
          <input
            value={form.bezeichnung}
            onChange={(e) => {
              setForm({ ...form, bezeichnung: e.target.value });
            }}
            style={{ minWidth: '14rem' }}
          />
        </label>
        <label>
          Preis brutto €
          <input
            inputMode="decimal"
            value={form.preis}
            onChange={(e) => {
              setForm({ ...form, preis: e.target.value });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                kachelHinzufuegen();
              }
            }}
          />
        </label>
        <label>
          Steuersatz
          <select
            value={form.steuersatz}
            onChange={(e) => {
              setForm({ ...form, steuersatz: e.target.value as Steuersatz });
            }}
          >
            {STEUERSAETZE.map((s) => (
              <option key={s.satz} value={s.satz}>
                {s.text}
              </option>
            ))}
          </select>
        </label>
        <label>
          Satz außer Haus (optional)
          <select
            value={form.steuersatzAusserHaus}
            onChange={(e) => {
              setForm({ ...form, steuersatzAusserHaus: e.target.value as Steuersatz | '' });
            }}
          >
            <option value="">wie im Haus</option>
            {STEUERSAETZE.map((s) => (
              <option key={s.satz} value={s.satz}>
                {s.text}
              </option>
            ))}
          </select>
        </label>
        <IconAuswahl
          wert={form.icon}
          label="Icon (sonst Gruppe)"
          onChange={(icon) => {
            setForm({ ...form, icon });
          }}
        />
        <button className="taste" onClick={kachelHinzufuegen}>
          Kachel hinzufügen
        </button>
      </div>
      {fehler !== null && <p className="fehler">{fehler}</p>}
      {konfig.kacheln.length > 0 && (
        <table className="liste">
          <thead>
            <tr>
              <th>Gruppe</th>
              <th>Bezeichnung</th>
              <th className="betrag">Preis</th>
              <th>USt</th>
              <th className="aktionen" />
            </tr>
          </thead>
          <tbody>
            {konfig.kacheln.map((k, i) => (
              <tr key={`${String(i)}-${k.bezeichnung}`}>
                <td>
                  <span
                    style={{
                      ...farbfeld('blau'),
                      background: kachelFarbe(k, konfig.gruppen),
                    }}
                  />
                  {k.gruppe}
                </td>
                <td>{k.bezeichnung}</td>
                <td className="betrag">{formatiereEuro(k.bruttoCent)} €</td>
                <td>
                  {k.steuersatz} %
                  {k.steuersatzAusserHaus !== undefined && ` / AH ${k.steuersatzAusserHaus} %`}
                </td>
                <td className="aktionen">
                  <button
                    className="taste zweit"
                    title="nach oben"
                    aria-label="nach oben"
                    onClick={() => {
                      verschiebe(konfig.kacheln, i, -1);
                    }}
                  >
                    ↑
                  </button>{' '}
                  <button
                    className="taste zweit"
                    title="nach unten"
                    aria-label="nach unten"
                    onClick={() => {
                      verschiebe(konfig.kacheln, i, 1);
                    }}
                  >
                    ↓
                  </button>{' '}
                  <button
                    className="taste zweit"
                    title="entfernen"
                    aria-label="entfernen"
                    onClick={() => {
                      speichern({ ...konfig, kacheln: konfig.kacheln.filter((_, j) => j !== i) });
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
